import React, { useState, useCallback } from 'react';
import { Location, Path } from '../../../types';
import { Plus, X } from 'lucide-react';

interface PathManagerProps {
  locations: Location[];
  paths: Path[];
  onAddPath: (startLocationId: string, endLocationId: string, waypoints: { x: number; y: number }[]) => Promise<string>;
  onUpdatePath: (pathId: string, waypoints: { x: number; y: number }[]) => Promise<void>;
  onDeletePath: (pathId: string) => Promise<void>;
  onClose: () => void;
}

export const PathManager: React.FC<PathManagerProps> = ({
  locations,
  paths,
  onAddPath,
  onUpdatePath: _onUpdatePath,
  onDeletePath,
  onClose,
}) => {
  const [selectedStartLocation, setSelectedStartLocation] = useState<string | null>(null);
  const [selectedEndLocation, setSelectedEndLocation] = useState<string | null>(null);
  const [isCreatingPath, setIsCreatingPath] = useState(false);
  const [editingPathId, setEditingPathId] = useState<string | null>(null);

  const handleStartLocationSelect = (locationId: string) => {
    setSelectedStartLocation(locationId);
    setSelectedEndLocation(null);
  };

  const handleEndLocationSelect = (locationId: string) => {
    if (locationId === selectedStartLocation) return;
    setSelectedEndLocation(locationId);
  };
  const handleCreatePath = useCallback(async () => {
    if (selectedStartLocation && selectedEndLocation) {
      const startLocation = locations.find(loc => loc.id === selectedStartLocation);
      const endLocation = locations.find(loc => loc.id === selectedEndLocation);
      
      if (startLocation && endLocation) {
        try {
          // Create a direct path between locations
          await onAddPath(
            selectedStartLocation,
            selectedEndLocation,
            [] // No waypoints initially
          );
          
          // Reset selection
          setSelectedStartLocation(null);
          setSelectedEndLocation(null);
          setIsCreatingPath(false);
        } catch (error) {
          console.error('Failed to create path:', error);
        }
      }
    }
  }, [selectedStartLocation, selectedEndLocation, locations, onAddPath]);

  const handleEditPath = (pathId: string) => {
    setEditingPathId(pathId);
  };

  const handleCancelEdit = () => {
    setEditingPathId(null);
  };
  const handleDeletePath = async (pathId: string) => {
    try {
      await onDeletePath(pathId);
      setEditingPathId(null);
    } catch (error) {
      console.error('Failed to delete path:', error);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-4">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-semibold text-stone-800">Path Management</h2>
        <div className="flex gap-2">
          <button
            onClick={() => setIsCreatingPath(!isCreatingPath)}
            className={`p-2 rounded-full transition-colors ${
              isCreatingPath 
                ? 'bg-amber-100 text-amber-600' 
                : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
            }`}
            title={isCreatingPath ? "Cancel Path Creation" : "Create New Path"}
          >
            <Plus className="h-5 w-5" />
          </button>
          <button
            onClick={onClose}
            className="p-2 rounded-full bg-stone-100 text-stone-600 hover:bg-stone-200 transition-colors"
            title="Close Path Manager"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
      </div>

      {isCreatingPath && (
        <div className="space-y-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">
              Start Location
            </label>
            <div className="grid grid-cols-2 gap-2">
              {locations.map(location => (
                <button
                  key={location.id}
                  onClick={() => handleStartLocationSelect(location.id)}
                  className={`p-2 rounded text-sm text-left ${
                    selectedStartLocation === location.id
                      ? 'bg-amber-100 text-amber-800 border-amber-300'
                      : 'bg-stone-50 text-stone-700 hover:bg-stone-100'
                  } border`}
                >
                  {location.name}
                </button>
              ))}
            </div>
          </div>

          {selectedStartLocation && (
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">
                End Location
              </label>
              <div className="grid grid-cols-2 gap-2">
                {locations
                  .filter(loc => loc.id !== selectedStartLocation)
                  .map(location => (
                    <button
                      key={location.id}
                      onClick={() => handleEndLocationSelect(location.id)}
                      className={`p-2 rounded text-sm text-left ${
                        selectedEndLocation === location.id
                          ? 'bg-amber-100 text-amber-800 border-amber-300'
                          : 'bg-stone-50 text-stone-700 hover:bg-stone-100'
                      } border`}
                    >
                      {location.name}
                    </button>
                  ))}
              </div>
            </div>
          )}

          {selectedStartLocation && selectedEndLocation && (
            <button
              onClick={handleCreatePath}
              className="w-full py-2 px-4 bg-amber-600 text-white rounded-md hover:bg-amber-700 transition-colors"
            >
              Create Path
            </button>
          )}
        </div>
      )}

      <div className="space-y-2">
        <h3 className="text-sm font-medium text-stone-700">Existing Paths</h3>
        {paths.length === 0 ? (
          <p className="text-sm text-stone-500">No paths created yet</p>
        ) : (
          paths.map(path => {
            const startLocation = locations.find(loc => loc.id === path.startLocationId);
            const endLocation = locations.find(loc => loc.id === path.endLocationId);
            
            return (
              <div
                key={path.id}
                className="flex items-center justify-between p-2 bg-stone-50 rounded border border-stone-200"
              >
                <div className="text-sm">
                  <span className="font-medium">{startLocation?.name}</span>
                  <span className="mx-2 text-stone-400">→</span>
                  <span className="font-medium">{endLocation?.name}</span>
                </div>
                <div className="flex gap-2">
                  {editingPathId === path.id ? (
                    <>
                      <button
                        onClick={() => handleDeletePath(path.id)}
                        className="p-1 text-red-600 hover:bg-red-50 rounded"
                        title="Delete Path"
                      >
                        <X className="h-4 w-4" />
                      </button>
                      <button
                        onClick={handleCancelEdit}
                        className="p-1 text-stone-600 hover:bg-stone-100 rounded"
                        title="Cancel"
                      >
                        Cancel
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={() => handleEditPath(path.id)}
                      className="p-1 text-stone-600 hover:bg-stone-100 rounded"
                      title="Edit Path"
                    >
                      Edit
                    </button>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}; 